import { NICHT_VORHANDEN, api, ohne404 } from '../api'
import { datum } from '../format'
import { useAktion, useLaden } from '../hooks'
import { WAECHTERART } from '../texte'
import Meldung from './Meldung'
import Meldungen from './Meldungen'
import { waechterWorauf } from './OffenesAusDemAssistenten'
import type { Waechter } from '../typen'

/** Der Stand eines Wächters in Worten: wartet noch, hat angeschlagen, gesehen. */
function stand(w: Waechter): { text: string; klasse: string } {
  if (w.ausgeloest_am && !w.gesehen_am) return { text: `angeschlagen am ${datum(w.ausgeloest_am)}`, klasse: 'fehler' }
  if (w.ausgeloest_am) return { text: `angeschlagen am ${datum(w.ausgeloest_am)}, gesehen`, klasse: 'erledigt' }
  if (!w.aktiv) return { text: 'aus', klasse: 'verworfen' }
  return { text: 'passt auf', klasse: 'offen' }
}

/**
 * Die Wächter (02.11.2026): „Warne mich, sobald 10713 in der Produktionsplanung auftaucht" - der
 * Assistent legt den Auftrag an, die Plattform prüft ihn selbst, und angeschlagen steht er auf „Heute".
 * Hier die ganze Liste mit dem Stand; was nicht mehr gebraucht wird, wird gelöscht.
 */
export default function Waechterliste() {
  const { daten: geholt, fehler: ladeFehler, neuLaden } = useLaden<Waechter[] | typeof NICHT_VORHANDEN>(() => ohne404(api.waechter()), [])
  const { fehler, erfolg, arbeitet, ausfuehren } = useAktion()
  if (geholt === NICHT_VORHANDEN) return <p className="hinweis">Wächter kennt das Backend noch nicht.</p>

  function loeschen(w: Waechter) {
    if (!window.confirm(`Wächter „${w.text.slice(0, 80)}“ löschen? Die Plattform passt dann nicht mehr darauf auf.`)) return
    void ausfuehren(async () => {
      await api.waechterLoeschen(w.id)
      neuLaden()
    }, 'Wächter gelöscht.')
  }

  return (
    <div className="karte">
      <div className="leiste">
        <b>Wächter</b>
        <span className="hinweis">
          Aufträge „sag mir, wenn …“ - der Assistent legt sie im Gespräch an; angeschlagen stehen sie auf „Heute“.
        </span>
      </div>
      {ladeFehler && <Meldung art="fehler">Die Wächter ließen sich nicht laden: {ladeFehler}</Meldung>}
      <Meldungen fehler={fehler} erfolg={erfolg} />
      <div className="tabellenrahmen">
        <table className="entscheidungen-tabelle">
          <thead>
            <tr>
              <th>Was</th>
              <th>Art</th>
              <th>Worauf</th>
              <th>Von, seit</th>
              <th>Stand</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {geholt && geholt.length === 0 && (
              <tr>
                <td colSpan={6} className="leer">
                  Keine Wächter.
                </td>
              </tr>
            )}
            {(geholt ?? []).map((w) => {
              const s = stand(w)
              return (
                <tr key={w.id} className={w.aktiv || w.ausgeloest_am ? undefined : 'leise'}>
                  <td>
                    {w.text}
                    {w.ausloeser && <div className="hinweis">{w.ausloeser}</div>}
                  </td>
                  <td>{WAECHTERART[w.art] ?? w.art}</td>
                  <td>{waechterWorauf(w)}</td>
                  <td>
                    {w.angelegt_von ?? ''}
                    <div className="hinweis">{datum(w.angelegt_am)}</div>
                  </td>
                  <td>
                    <span className={`status ${s.klasse}`}>{s.text}</span>
                  </td>
                  <td>
                    <button className="leise" disabled={arbeitet} onClick={() => loeschen(w)}>
                      Löschen
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
